/**
 * Sorting the Report view on a column header click.
 *
 * FrameTracker prints in its own order; a header click re-sorts the rows on one
 * column (ascending, then descending, then back to FrameTracker's order). Sorted
 * rows no longer arrive in group order, so the runner also drops the layout's
 * grouping while a sort is on (sortedLayout).
 */
import {NUMERIC_TYPES} from './reportCells'

const DATE_TYPES = ['date', 'datetime']

function isBlank(value) {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
}

function comparable(value, type) {
    if (NUMERIC_TYPES.includes(type)) {
        const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[$,\s]/g, ''))
        return Number.isNaN(n) ? null : n
    }
    if (DATE_TYPES.includes(type)) {
        const t = Date.parse(value)
        return Number.isNaN(t) ? String(value) : t
    }
    return String(value)
}

const textOrder = new Intl.Collator('en-US', {numeric: true, sensitivity: 'base'})

function compareValues(a, b) {
    if (typeof a === 'number' && typeof b === 'number') return a - b
    return textOrder.compare(String(a), String(b))
}

/** The next state for a click on `key`: ascending, descending, then off. */
export function nextSort(current, key) {
    if (!current || current.key !== key) return {key, dir: 'asc'}
    if (current.dir === 'asc') return {key, dir: 'desc'}
    return {key: null, dir: null}
}

/**
 * The rows sorted on one column, as a new array. Stable (ties keep
 * FrameTracker's order) and blanks last in either direction.
 *
 * types: {key: column} from the report's columns, for numeric and date values.
 */
export function sortRows(rows, sort, types = {}) {
    if (!sort || !sort.key || !Array.isArray(rows)) return rows
    const type = (types[sort.key] || {}).type
    const sign = sort.dir === 'desc' ? -1 : 1
    return rows
        .map((row, index) => {
            const raw = row ? row[sort.key] : null
            // 'days' prints -1 as blank (formatCell), so it sorts as blank too.
            const blank = isBlank(raw) || (type === 'days' && Number(raw) === -1)
            const value = blank ? null : comparable(raw, type)
            return {row, index, value}
        })
        .sort((a, b) => {
            if (a.value === null || b.value === null) {
                if (a.value === null && b.value === null) return a.index - b.index
                return a.value === null ? 1 : -1
            }
            return sign * compareValues(a.value, b.value) || a.index - b.index
        })
        .map(entry => entry.row)
}

/** A layout without its group breaks, for rows that are no longer in group order. */
export function sortedLayout(layout) {
    if (!layout) return layout
    // eslint-disable-next-line no-unused-vars
    const {groupBy, groupLevels, ...flat} = layout
    return flat
}
